/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderUserContext,
  CommonActions,
  CommunicationEntryPoint,
  EmailDialogConfig,
  emailDialogRoute,
  FormActionPipelines,
  FormFlowState,
  getSalesTaxRateListItemId,
  GraphicalTakeOffMainConfig,
  graphicalTakeOffMainDialogRoute,
  HousePlanFinderDialogConfig,
  housePlanFinderDialogRoute,
  PbComponentItemFinderDialogConfig,
  pbComponentItemFinderDialogRoute,
  PbComponentItemFinderListItem,
  PbHousePlanFinderDialogConfig,
  pbHousePlanFinderDialogRoute,
  SalesTaxRateFinderDialogConfig,
  salesTaxRateFinderDialogRoute,
  SalesTaxRateListItem,
} from '@gh/builder-shared';
import {
  attachmentFromLetter,
  CommunicationType,
  createLinkFromLetter,
  HpeInitType,
  isDeliveryMethodEmail,
  QuotationBase,
} from '@gh/builder-shared-data';
import { textMessageDialogRoute } from '@gh/common-internal-shared';
import {
  ActionFlow,
  AppRouter,
  ContainerState,
  FormStateSnapshot,
  getAllNewSelectedEntities,
  getAllSelectedEntities,
  getFormStateSnapshotValue,
  getSingleNewSelectedEntity,
  ImmutablePageState,
  mutate,
  newSelectionModel,
  PAGE_STATE,
  queryDirty,
  queryFormMode,
  queryVersion,
  setFormStateSnapshotValue,
  State,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import {
  Confirmation,
  DialogService,
  FormMode,
  isCreateFormMode,
  NotificationService,
  TextMessageDialogConfig,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { createLink, differentFieldsWith } from '@gh/core-util';
import { EMPTY$, of$ } from '@gh/rx';
import { filter$, map$, mapTo$, switchMap$ } from '@gh/rx/operators';

import { first, isEmpty, isNil, map, omit } from 'lodash';
import { Observable } from 'rxjs';

import {
  QuoteCloneDataDialogConfig,
  quoteCloneDataDialogRoute,
  QuoteCloneDialogEntryPoint,
} from '../quote-clone-data-dialog/quote-clone-data-dialog.component';
import { rfqQuoteFilterDialogRoute } from '../rfq-quote-filter-dialog/rfq-quote-filter-dialog.component';
import { QuoteDetailsBusinessService, QuoteTopDialogConfig } from '../../resources/quote-details-business.service';
import {
  QuoteDetailsInitResponse,
  QuoteDetailsMasterInfo,
  QuoteDetailsSaveResponse,
  QuoteHpFinderListItem,
  QuotePrintFilter,
  quoteSalesTaxRateFromFinder,
  quoteSalesTaxRateToFinder,
  QuoteVariation,
  quoteVariationsFromFinder,
  QuoteVariationsIdsGetResponse,
  useZeroPricesForHp,
} from '../../resources/quote.model';
import {
  QuoteStructureConfig,
  quoteStructureDialogRoute,
} from '../lead-details/quote-structure-dialog';
import { quoteTopDialogRoute } from '../quote-top-dialog/quote-top-dialog.component';
import {
  calculateTopDerivedInfo,
  queryQuotationBase,
  queryQuoteBaseHousePlan,
  queryQuoteContactId,
  queryQuoteDerivedInfo,
  queryQuoteHasErrors,
  queryQuoteHasNotPriceBookVariations,
  queryQuoteHousePlan,
  queryQuoteId,
  queryQuoteIsProduced,
  queryQuoteMasterInfo,
  queryQuoteMasterInfoShadow,
  queryQuotePriceBook,
  queryQuotePrintProduceRequest,
  queryQuoteRecalculateModel,
  queryQuoteSalesFolderId,
  queryQuoteSaveModel,
  queryQuoteSettings,
  queryQuoteTopFormHasErrors,
  queryQuoteVariation,
  queryQuoteVariations,
  QuoteDetailsState,
  QuoteDetailsStateEventType, VariationsFilter,
} from './quote-details.state';

export type QuoteSaveFlowState = FormFlowState<QuoteDetailsState> & { saveResponse?: QuoteDetailsSaveResponse };

export enum QuoteMasterInfoChange { AutoApproved = 1, Approved = 2, Rejected = 3 }

@Injectable()
export class QuoteDetailsCommandService {
  constructor(private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private businessService: QuoteDetailsBusinessService,
              @Inject(PAGE_STATE) private form: ImmutablePageState<QuoteDetailsState>,
              private state: State<QuoteDetailsState>,
              private userContext: BuilderUserContext,
              private dialogService: DialogService,
              private notificationService: NotificationService,
              private waitingService: WaitingService,
              private translationService: TranslationService,
              private route: ActivatedRoute,
              private router: AppRouter) {

  }

  load(init: QuoteDetailsInitResponse, mode: FormMode): void {
    const supportsProfit = this.businessService.isCurrentUserSupportsProfit;

    this.state.mutate({
      type: QuoteDetailsStateEventType.Load,
      init,
      supportsProfit,
      isProfitVisible: false,
      mode,
    });
  }

  print(): void {
    if (this.state.project(queryQuoteHasErrors)) {
      this.handleErrors();
      return;
    }

    if (!this.ensureSaved()) {
      return;
    }

    if (this.form.editable && !this.state.project(queryQuoteHasNotPriceBookVariations)) {
      this.notificationService.error('msg_no_in_price_book');
      this.state.mutate({ type: QuoteDetailsStateEventType.NavigateToNotPriceBookVariation });
      return;
    }

    const salesFolderId = this.state.project(queryQuoteSalesFolderId);
    const quotationId = this.state.project(queryQuoteId)!; // tslint:disable-line:no-non-null-assertion
    const customerDeliveryBy = this.state.project(queryQuoteDerivedInfo).deliveryBy;
    const quotationBase = getFormStateSnapshotValue(this.state.project(queryQuoteMasterInfo)).base;
    const supportsProfit = this.state.project(queryQuoteSettings).supportsProfit;
    const isProfitVisible = this.state.project(queryQuoteSettings).isProfitVisible;

    this.dialogService.openWithInput(rfqQuoteFilterDialogRoute, {
      base: quotationBase,
      deliveryBy: customerDeliveryBy,
    }).afterClosed().pipe(
      filter$(Boolean),
      switchMap$((filter: QuotePrintFilter) =>
        this.businessService.print(this.state.project((s) => queryQuotePrintProduceRequest(s, filter))).pipe(
          this.waitingService.operator(),
          map$((response) => ({ ...response, deliveryBy: filter.deliveryBy })))),
      switchMap$(({ quoteDoc, authorityDoc, hpTopReport, deliveryBy, extraAttachments }) => {
        if (isDeliveryMethodEmail(deliveryBy)) {
          return this.dialogService.openWithInput(emailDialogRoute, <EmailDialogConfig>{
            entryPoint: CommunicationEntryPoint.Lead,
            contactId: this.state.project(queryQuoteContactId),
            email: {
              email: this.state.project(queryQuoteDerivedInfo).email,
              subject: '',
              message: '',
              communicationType: CommunicationType.Email,
              attachments: [attachmentFromLetter(quoteDoc)]
                .concat(authorityDoc ? [attachmentFromLetter(authorityDoc)] : [])
                .concat(extraAttachments || []),
              contactId: this.state.project(queryQuoteContactId),
            },
          }).afterClosed();
        } else {
          return this.dialogService.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
            title: this.translationService.get('lbl_link_to_print_quote_document'),
            html: [this.translationService.get('lbl_text_print_quote_document', {
              url: createLinkFromLetter(quoteDoc),
            })]
              .concat(authorityDoc
                ? [this.translationService.get('lbl_text_print_authority_document', {
                  url: createLinkFromLetter(authorityDoc),
                })]
                : [])
              .concat(hpTopReport
                        ? [this.translationService.get('lbl_text_print_top_document', {
                  url: createLinkFromLetter(hpTopReport),
                })]
                 : [])
              .concat(!isEmpty(extraAttachments)
                ? [this.translationService.get('lbl_rfq_attachments')]
                  .concat(map(extraAttachments, ({ webViewLink }) => createLink(webViewLink)))
                  .join('<br/>')
                : [])
              .join('<br/><br/>'),
            buttons: [{ value: 'ok', title: this.translationService.get('lbl_ok'), type: 'primary' }],
          }).afterClosed();
        }
      }),
      switchMap$(() => this.businessService.get(salesFolderId, quotationId).pipe(
        this.waitingService.operator())))
      .subscribe(
        (init) => this.state.mutate({
          type: QuoteDetailsStateEventType.Load,
          init,
          supportsProfit,
          isProfitVisible,
          mode: FormMode.ViewMode,
        }),
        this.notificationService.processAll,
      );
  }

  autoSave(form: ContainerState<QuoteDetailsState>): Observable<any> {
    if (!this.state.project(queryDirty)) {
      return EMPTY$;
    }

    return this.dialogService.confirmation(yesNoConfirmation('msg_unsaved_info')).pipe(
      filter$(yesAnswer),
      switchMap$(() => {
        const flow = this.completeFlow();

        return flow.run({ trigger: 'process', value: this.state.value, form }).toFinalState();
      }));
  }

  setMasterInfo(snapshot: FormStateSnapshot<QuoteDetailsMasterInfo>): void {
    this.state.mutate({ type: QuoteDetailsStateEventType.SetMasterInfoShadow, snapshot });

    const masterInfo = getFormStateSnapshotValue(this.state.project(queryQuoteMasterInfo));
    const masterInfoShadow = getFormStateSnapshotValue(this.state.project(queryQuoteMasterInfoShadow));

    const modifiedField = first(differentFieldsWith(masterInfo, masterInfoShadow));
    switch (modifiedField) {
      case 'priceBook':
        this.tryMasterInfoChange(['base', 'baseHousePlan'], 'msg_quote_change_pb');
        break;
      case 'base':
        this.tryMasterInfoChange(['baseHousePlan'], 'msg_quote_change_base');
        break;
      case 'baseHousePlan':
        this.tryMasterInfoChange([], 'msg_quote_change_hp');
        break;
      default:
        this.state.mutate({ type: QuoteDetailsStateEventType.CommitMasterInfo });
        break;
    }
  }

  cloneVariations(): void {
    if (!this.ensureSaved()) {
      return;
    }

    const { contactId, customerName } = this.state.project(queryQuoteDerivedInfo);

    this.dialogService.openWithInput(quoteCloneDataDialogRoute, <QuoteCloneDataDialogConfig>{
      contactId,
      customerName,
      entryPoint: QuoteCloneDialogEntryPoint.CloneChanges,
      targetQuoteId: this.state.project(queryQuoteId),
      targetQuoteVersion: this.state.project(queryVersion),
      salesFolderId: this.state.project(queryQuoteSalesFolderId),
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((init: any) => this.load(init, this.form.mode));
  }

  addVariations(): void {
    if (!this.ensureEditMode()) {
      return;
    }

    const pb = this.state.project(queryQuotePriceBook)!; // tslint:disable-line:no-non-null-assertion
    const baseHp = this.state.project(queryQuoteBaseHousePlan)!; // tslint:disable-line:no-non-null-assertion
    const hp = this.state.project(queryQuoteHousePlan)!; // tslint:disable-line:no-non-null-assertion
    const base = this.state.project(queryQuotationBase);

    this.dialogService.openWithInput(pbComponentItemFinderDialogRoute, <PbComponentItemFinderDialogConfig>{
      multiselection: true,
      priceBookId: pb.id,
      priceBookName: pb.name,
      housePlanId: base === QuotationBase.HouseEstimate ? baseHp && baseHp.housePlanId : hp.housePlanId,
      housePlanName: base === QuotationBase.HouseEstimate ? baseHp && baseHp.code : hp.code,
      isNumericSelector: true,
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities),
      switchMap$((items: PbComponentItemFinderListItem[]) => {
        const duplicatedItems: PbComponentItemFinderListItem[] = items.reduce((acc, item) => {
          const count = item.count || 1;
          const copies = Array(count).fill(null).map(() => ({ ...item }));
          return acc.concat(copies);
        }, [] as PbComponentItemFinderListItem[]);

        return this.businessService.generateVariationIds(
          this.state.project(queryQuoteSalesFolderId),
          this.state.project(queryQuoteId)!, // tslint:disable-line:no-non-null-assertion
          duplicatedItems.filter((item) => item.isComponent).length,
          duplicatedItems.filter((item) => !item.isComponent).length).pipe(
            this.waitingService.operator<QuoteVariationsIdsGetResponse>(),
            map$((generatedIdSets) => quoteVariationsFromFinder(generatedIdSets, duplicatedItems)))
      }))
      .subscribe((variations: QuoteVariation[]) => {
        this.state.mutate({ type: QuoteDetailsStateEventType.VariationAddSet, variations });
      });
  }
  cloneQuoteVariation(variationId: ID): void {
    const variation = this.state.project((s) => queryQuoteVariation(s, variationId));
    if (variation) {
      this.businessService.generateVariationIds(
        this.state.project(queryQuoteSalesFolderId),
        this.state.project(queryQuoteId)!, 
        variation.isComponent ? 1 : 0,
        variation.isComponent ? 0 : 1
      ).pipe(
        this.waitingService.operator<QuoteVariationsIdsGetResponse>(),
        map$((generatedIdSets) => {
          const newId = variation.isComponent ? 
            generatedIdSets.componentIds[0] : 
            generatedIdSets.itemIds[0];

          const clonedVariation = {
            ...variation,
            quotationItemId: newId,
            variationId: `item:${newId}`
          };
          return [clonedVariation];
        })
      ).subscribe((variations: QuoteVariation[]) => {
        this.state.mutate({ 
          type: QuoteDetailsStateEventType.VariationAddSet, 
          variations
        });
      });
    }
  }

  moveVariationCreditDebit(variationId: ID): void {
    this.state.mutate({ type: QuoteDetailsStateEventType.VariationMoveCreditDebit, variationId });
  }

  viewVariationMemo(variationId: ID): void {
    const variation = this.state.project((s) => queryQuoteVariation(s, variationId));

    if (variation) {
      this.dialogService.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
        title: this.translationService.get('lbl_memo'),
        text: variation.memo,
        buttons: [{ value: 'ok', title: this.translationService.get('lbl_ok'), type: 'primary' }],
      });
    }
  }

  removeVariation(variationId: ID): void {
    const variation = this.state.project((s) => queryQuoteVariation(s, variationId));
    if (isNil(variation)) {
      return;
    }

    this.dialogService.confirmation(yesNoConfirmation('msg_quote_remove_item_component',
                                                      { code: variation.itemCode })).pipe(
      filter$(yesAnswer),
      mapTo$({ type: QuoteDetailsStateEventType.VariationRemove, variationId }),
      mutate(this.state))
      .subscribe();
  }

  setVariationField(variationId: ID, name: string, value: any): void {
    this.state.mutate({ type: QuoteDetailsStateEventType.VariationSetField, variationId, name, value });
  }

  resetFocusedCell(): void {
    this.state.mutate({ type: QuoteDetailsStateEventType.ResetFocusedCell });
  }

  setMemo(memo: string): void {
    this.state.mutate({ type: QuoteDetailsStateEventType.SetBottomField, name: 'memo', value: memo });
  }

  setMemoOnTop(isMemoOnTop: boolean): void {
    this.state.mutate({ type: QuoteDetailsStateEventType.SetBottomField, name: 'isMemoOnTop', value: isMemoOnTop });
  }

  searchHousePlan(): void {
    // verify that can we change this quote
    this.confirmMasterInfoChange([], 'msg_quote_change_hp').pipe(
      filter$(Boolean),
      filter$((answer) => answer !== QuoteMasterInfoChange.Rejected),
      // show house plan finder
      switchMap$(() => {
        const priceBook = this.state.project(queryQuotePriceBook);
        const baseHousePlan = this.state.project(queryQuoteBaseHousePlan);

        switch (this.state.project(queryQuotationBase)) {
          case QuotationBase.QuickQuote:
            return this.dialogService.openWithInput(pbHousePlanFinderDialogRoute, <PbHousePlanFinderDialogConfig>{
              priceBookId: priceBook!.id, // tslint:disable-line:no-non-null-assertion
              priceBookName: priceBook!.name, // tslint:disable-line:no-non-null-assertion
              filter: { allowQq: true, isSalesEstimate: true },
              preselected: baseHousePlan ? [baseHousePlan.housePlanId] : void 0,
              multiselection: false,
            }).afterClosed().pipe(filter$(Boolean), map$(getSingleNewSelectedEntity), map$(useZeroPricesForHp));
          case QuotationBase.HouseEstimate:
            return this.dialogService.openWithInput(pbHousePlanFinderDialogRoute, <PbHousePlanFinderDialogConfig>{
              priceBookId: priceBook!.id, // tslint:disable-line:no-non-null-assertion
              priceBookName: priceBook!.name, // tslint:disable-line:no-non-null-assertion
              filter: { isSalesEstimate: true },
              preselected: baseHousePlan ? [baseHousePlan.housePlanId] : void 0,
              multiselection: false,
            }).afterClosed().pipe(filter$(Boolean), map$(getSingleNewSelectedEntity));
          case QuotationBase.SalesEstimate:
            return this.dialogService.openWithInput(housePlanFinderDialogRoute, <HousePlanFinderDialogConfig>{
              disableShowQqSwitcher: true,
              disableShowSeSwitcher: true,
              filter: { hideEmpty: true },
              preselected: baseHousePlan ? [baseHousePlan.housePlanId] : void 0,
            }).afterClosed().pipe(filter$(Boolean), map$(getSingleNewSelectedEntity), map$(useZeroPricesForHp));
          default:
            return EMPTY$;
        }
      }))
      .subscribe((baseHousePlan: QuoteHpFinderListItem) => {
        // set house plan into top form snapshot
        this.state.mutate({
          type: QuoteDetailsStateEventType.SetMasterInfoShadow,
          snapshot: setFormStateSnapshotValue(
            this.state.project(queryQuoteMasterInfoShadow),
            (currentShadow) => ({ ...currentShadow, baseHousePlan })),
          derivedInfo: calculateTopDerivedInfo(
            this.state.project(queryQuoteDerivedInfo),
            baseHousePlan.salesPrice,
            baseHousePlan.salesPriceWithoutGst),
        });

        this.state.mutate({ type: QuoteDetailsStateEventType.CommitMasterInfo, resetVariations: true });
      });
  }

  openStr(): void {
    if (!this.ensureEditMode()) {
      return;
    }

    const masterInfo = getFormStateSnapshotValue(this.state.project(queryQuoteMasterInfo));

    this.dialogService.openWithInput(salesTaxRateFinderDialogRoute, <SalesTaxRateFinderDialogConfig>{
        multiselection: true,
        selectionModel: newSelectionModel(
          (masterInfo.salesTaxRates || []).map(quoteSalesTaxRateToFinder),
          getSalesTaxRateListItemId),
        unselectable: true,
      })
      .afterClosed().pipe(
      filter$(Boolean),
      map$(getAllSelectedEntities))
      .subscribe((salesTaxRates: SalesTaxRateListItem[]) => {
        // set house plan into top form snapshot
        this.state.mutate({
          type: QuoteDetailsStateEventType.SetMasterInfoShadow,
          snapshot: setFormStateSnapshotValue(
            this.state.project(queryQuoteMasterInfoShadow),
            (currentShadow) => ({
              ...currentShadow,
              salesTaxRates: salesTaxRates.map(quoteSalesTaxRateFromFinder),
            })),
        });

        this.state.mutate({ type: QuoteDetailsStateEventType.CommitMasterInfo });
      });
  }

  openGto(isDisabledGto: boolean): void {
    const mode = this.state.project(queryFormMode);

    switch (mode) {
      case FormMode.CreateMode:
        this.notificationService.error('msg_quote_create_process');
        break;
      case FormMode.EditMode: {
        // In Edit mode, GTO is opened only after all current changes were saved.
        if (this.state.project(queryDirty)) {
          this.dialogService.confirmation(yesNoConfirmation('msg_continue_will_save_changes')).pipe(
            filter$(yesAnswer),
            switchMap$(() => {
              const flow = this.completeFlow();

              return flow.run({ trigger: 'process', value: this.state.value, form: this.form })
                .toFinalState().pipe(
                  // we have to guarantee that GTO is opened only after successful save
                  filter$((s) => !!s.success));
            })).subscribe(() => {
            this.showGto(isDisabledGto);
          });
        } else {
          this.showGto(isDisabledGto);
        }

        break;
      }
      default:
        // in View mode, we allow to open GTO only if there is at least one measured ToP
        if (this.state.project(queryQuoteSettings).isGtoTopExist) {
          this.showGto(isDisabledGto);
        } else {
          this.notificationService.warning('msg_no_gto_measurements');
        }
        break;
    }
  }

  openHpe(): void {
    if (!this.ensureSaved()) {
      return;
    }

    const housePlan = this.state.project(queryQuoteHousePlan);

    if (isNil(housePlan)) {
      return;
    }

    this.router.navigateToChild(['/estimating/house-plans', housePlan.housePlanId, 'estimate'], {
      parentTitle: {
        [FormMode.CreateMode]: 'lbl_quote_create',
        [FormMode.EditMode]: 'lbl_quote_edit',
        [FormMode.ViewMode]: 'lbl_quote_view',
      }[this.state.project(queryFormMode)],
      queryParams: {
        initType: HpeInitType.Open,
        priceBookId: this.state.project(queryQuotePriceBook)!.id, // tslint:disable-line:no-non-null-assertion
      },
    });
  }

  openTops(formMode: number): void {
    if (!this.ensureSaved()) {
      return;
    }

    const housePlan = this.state.project(queryQuoteHousePlan);
    const priceBook = this.state.project(queryQuotePriceBook);

    if (housePlan && priceBook) {

      this.dialogService.openWithInput(quoteTopDialogRoute, <QuoteTopDialogConfig>{
        housePlanId: housePlan.housePlanId,
        priceBookId: priceBook.id,
        formMode,
      }).afterClosed().pipe(
        filter$(Boolean),
        switchMap$(() => this.businessService.calculate(this.state.project(queryQuoteRecalculateModel)).pipe(
          this.waitingService.operator())))
        .subscribe((saveResponse) =>
          this.state.mutate({ type: QuoteDetailsStateEventType.MergeOnRecalculate, saveResponse }));
    }
  }

  openStructure(): void {
    if (!this.ensureSaved()) {
      return;
    }

    const sfId = this.state.project(queryQuoteSalesFolderId);
    const quoteId = this.state.project(queryQuoteId);
    const supportsProfit = this.state.project(queryQuoteSettings).supportsProfit;
    const isProfitVisible = this.state.project(queryQuoteSettings).isProfitVisible;

    this.dialogService.openWithInput(quoteStructureDialogRoute, <QuoteStructureConfig>{
      sfId,
      quoteId,
      base: getFormStateSnapshotValue(this.state.project(queryQuoteMasterInfo)).base,
      version: this.state.project(queryVersion),
    }).afterClosed().pipe(
      switchMap$(() => this.businessService.get(sfId, quoteId || '').pipe(
        this.waitingService.operator())))
      .subscribe(
        (init) => this.state.mutate({
          type: QuoteDetailsStateEventType.Load,
          init,
          supportsProfit,
          isProfitVisible,
        }),
        this.notificationService.processAll,
      );
  }

  approve(): void {
    if (!this.state.project(queryQuoteIsProduced)) {
      this.notificationService.error('msg_quote_not_printed');
      return;
    }

    this.businessService.approve(
      this.state.project(queryQuoteSalesFolderId),
      this.state.project(queryQuoteId)!, // tslint:disable-line:no-non-null-assertion
      {
        approve: true,
        version: this.state.project(queryVersion)!, // tslint:disable-line:no-non-null-assertion
      }).pipe(this.waitingService.operator())
      .subscribe(
        (details) => this.state.mutate({ type: QuoteDetailsStateEventType.Approved, details }),
        this.notificationService.processAll,
      );
  }

  unapprove(): void {
    if (!this.state.project(queryQuoteIsProduced)) {
      this.notificationService.error('msg_quote_not_printed');
      return;
    }

    this.businessService.approve(
      this.state.project(queryQuoteSalesFolderId),
      this.state.project(queryQuoteId)!, // tslint:disable-line:no-non-null-assertion
      {
        approve: false,
        version: this.state.project(queryVersion)!, // tslint:disable-line:no-non-null-assertion
      }).pipe(this.waitingService.operator<any>())
      .subscribe(
        (details) => this.state.mutate({ type: QuoteDetailsStateEventType.Unapproved, details }),
        this.notificationService.processAll,
      );
  }

  toggleProfitVisiblity(visible: boolean): void {
    this.state.mutate({
      type: QuoteDetailsStateEventType.SetProfitVisibility,
      visible,
    });
  }

  completeFlow(): ActionFlow<QuoteSaveFlowState> {
    return this.pipelines.complete({
      validateForm: this.commonActions.validateWithEffect(
        () => this.state.project(queryQuoteHasErrors),
        () => this.handleErrors()),
      execute: this.commonActions.async((state: QuoteSaveFlowState) =>
        this.businessService.save(this.state.project(queryQuoteSaveModel)).pipe(
          map$((saveResponse) => ({ ...state, saveResponse })))),
      finish: (state: QuoteSaveFlowState) => {
        const { saveResponse } = state;
        if (saveResponse) {
          this.state.mutate({ type: QuoteDetailsStateEventType.MergeOnSave, saveResponse });
        }

        if (isCreateFormMode(this.state.project(queryFormMode))) {
          this.router.switchTo([
            '/sales/sales-folders',
            this.state.project(queryQuoteSalesFolderId),
            'rfq',
            'quotations',
            this.state.project(queryQuoteId),
          ]);
        }
      },
    });
  }

  recalculateFlow(): ActionFlow<QuoteSaveFlowState> {
    return this.pipelines.recalculate({
      validateForm: this.commonActions.validateWithEffect(
        () => this.state.project(queryQuoteHasErrors),
        () => this.handleErrors()),
      execute: this.commonActions.async((state: QuoteSaveFlowState) =>
        this.businessService.calculate(this.state.project(queryQuoteRecalculateModel)).pipe(
          map$((saveResponse) => ({ ...state, saveResponse })))),
      finish: (state: QuoteSaveFlowState) => {
        const { saveResponse } = state;
        if (saveResponse) {
          this.state.mutate({ type: QuoteDetailsStateEventType.MergeOnRecalculate, saveResponse });
        }
      },
    });
  }

  setVariationListFilter(filter: VariationsFilter): void {
    this.state.mutate({ type: QuoteDetailsStateEventType.ChangeVariationListFilter, variationsFilter: filter });
  }

  private showGto(isDisabledGto: boolean): void {
    const hp = this.state.project(queryQuoteHousePlan);
    const salesFolderId = this.state.project(queryQuoteSalesFolderId);
    const quotationId = this.state.project(queryQuoteId)!; // tslint:disable-line:no-non-null-assertion
    const masterInfo = getFormStateSnapshotValue(this.state.project(queryQuoteMasterInfo));

    if (hp) {
      this.dialogService.openWithInput(
        graphicalTakeOffMainDialogRoute,
        <GraphicalTakeOffMainConfig>{
          id: hp.housePlanId,
          entryPoint: 'QQ',
          title: `${hp.code} ${hp.name}`,
          quickQuote: true,
          priceBookId: masterInfo.priceBook!.id, // tslint:disable-line:no-non-null-assertion
          gFolderId: masterInfo.customerSfPlansFolderId,
          isDisabledGto,
        },
        {
          panelClass: 'cdk-overlay-large',
        }).afterClosed().pipe(
        switchMap$(() => this.businessService.get(salesFolderId, quotationId).pipe(
          this.waitingService.operator())))
        .subscribe(
          (init) => this.state.mutate({
            type: QuoteDetailsStateEventType.Load,
            init,
          }),
          this.notificationService.processAll,
        );
    }
  }

  private handleErrors(): void {
    if (!this.state.project(queryQuoteTopFormHasErrors)) {
      this.notificationService.error('msg_he_incorrect_data_in_tables');
    }

    this.state.mutate({ type: QuoteDetailsStateEventType.NavigateToInvalid });
  }

  private confirmMasterInfoChange(dependentFields: string[], messageId: string): Observable<QuoteMasterInfoChange> {
    const masterInfoSnapshot = this.state.project(queryQuoteMasterInfo);
    const masterInfo = getFormStateSnapshotValue(masterInfoSnapshot);
    const hasVariations = this.state.project(queryQuoteVariations).length > 0;
    const hasDependentFieldsSet = dependentFields.some((field) => masterInfo[field]);

    if (hasVariations || hasDependentFieldsSet) {
      return this.dialogService.confirmation(yesNoConfirmation(messageId)).pipe(
        // @ts-ignore
        map$((answer) => ({
          [Confirmation.Yes]: QuoteMasterInfoChange.Approved,
          [Confirmation.No]: QuoteMasterInfoChange.Rejected,
        }[answer] || QuoteMasterInfoChange.Rejected)));
    } else {
      return of$(QuoteMasterInfoChange.AutoApproved);
    }
  }

  private tryMasterInfoChange(dependentFields: string[], messageId: string): void {
    const answer$ = this.confirmMasterInfoChange(dependentFields, messageId);

    answer$.pipe(
      filter$((answer) => answer === QuoteMasterInfoChange.Approved))
      .subscribe(() => {
        // resets all dependent fields in shadow state of master info form
        // resets also prices for current house plan
        this.state.mutate({
          type: QuoteDetailsStateEventType.SetMasterInfoShadow,
          snapshot: setFormStateSnapshotValue(
            this.state.project(queryQuoteMasterInfoShadow),
            (currentShadow) => <QuoteDetailsMasterInfo>omit(currentShadow, dependentFields)),
          derivedInfo: calculateTopDerivedInfo(this.state.project(queryQuoteDerivedInfo)),
        });
        // commits reset values
        this.state.mutate({ type: QuoteDetailsStateEventType.CommitMasterInfo, resetVariations: true });
      });

    answer$.pipe(
      filter$((answer) => answer === QuoteMasterInfoChange.Rejected))
      // resets to previous values
      .subscribe(() => this.state.mutate({
        type: QuoteDetailsStateEventType.SetMasterInfoShadow,
        snapshot: this.state.project(queryQuoteMasterInfo),
      }));

    answer$.pipe(
      filter$((answer) => answer === QuoteMasterInfoChange.AutoApproved))
      // just commits current shadow form, i. e. this change does not affect quotation a lot
      .subscribe(() => this.state.mutate({ type: QuoteDetailsStateEventType.CommitMasterInfo }));
  }

  private ensureSaved(): boolean {
    const mode = this.state.project(queryFormMode);
    const dirty = this.state.project(queryDirty);

    if (isCreateFormMode(mode)) {
      this.notificationService.error('msg_quote_create_process');
      return false;
    } else if (dirty) {
      this.notificationService.error('msg_save_first');
      return false;
    }

    return true;
  }

  private ensureEditMode(): boolean {
    const mode = this.state.project(queryFormMode);

    if (isCreateFormMode(mode)) {
      this.notificationService.error('msg_quote_create_process');
      return false;
    }

    return true;
  }
}
